import type { Moment } from 'moment'
import moment from 'moment'
import type { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'

import { mapTypeaheadStreamingDataToExtractedSearchParams } from 'api/mappers/Search/typeaheadStreamingMap'

import {
  INTL_DAY_MONTH_NAME,
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_LONG_MONTH,
  ISO_DATE_FORMAT,
  ISO_DATE_SHORT_FORMAT,
} from 'constants/dateFormats'
import {
  MAX_ADULTS,
  MAX_NUM_CHILDREN,
  MAX_ROOM_LIMIT,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA,
} from 'constants/offer'
import type { OfferListSortOption } from 'constants/offerListFilters'
import config from 'constants/config'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  ANYWHERE_NAME,
  ANYWHERE_PLACE_ID,
  bnblChildrenAgesDelimiter,
  DEFAULT_BNBL_QUERY_ADULTS,
  DEFAULT_FLEXIBLE_DURATION_RANGE,
  queryChildrenDelimiter,
  queryNullChildren,
} from 'constants/search'
import {
  queryFlexibleWithDate,
  queryKeyAdults,
  queryKeyChildrenAges,
  queryKeyAmenities,
  queryKeyBounds,
  queryKeyBundledOfferId,
  queryKeyCampaigns,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyChildren,
  queryKeyCustomerRatingGte,
  queryKeyDestinationId,
  queryKeyDestinationName,
  queryKeyDuration,
  queryKeyExperienceId,
  queryKeyExperienceName,
  queryKeyFilters,
  queryKeyFlexibleMonths,
  queryKeyFlexibleNights,
  queryKeyHolidayTypes,
  queryKeyHolidayTypesScoped,
  queryKeyInclusions,
  queryKeyInclusionGap,
  queryKeyTags,
  queryKeyChains,
  chainsDelimiter,
  queryKeySubChains,
  subChainsDelimiter,
  queryKeyLandmarkId,
  queryKeyLandmarkName,
  queryKeyMapPinCount,
  queryKeyMultiRoomMode,
  queryKeyPages,
  queryKeyPropertyId,
  queryKeyPropertyName,
  queryKeyPropertyTypes,
  queryKeyRateId,
  queryKeyRoomId,
  queryKeySelectedOfferIds,
  queryKeySortBy,
  queryKeyUrlOfferType,
  queryKeyUserSelectedFlexibleMonths,
  queryKeyLat,
  queryKeyLng,
  queryKeyTypeaheadId,
  queryKeyExtracting,
  queryKeyFareClass,
  queryKeyDepartureAirport,
  queryKeyPriceMode,
  queryKeyPriceGte,
  queryKeyPriceLte,
  queryKeyQuery,
  queryKeySemanticRerank,
  queryKeyFlights,
  queryKeyRoomOptionRateId,
  queryKeyRoomRateGroupId,
  queryKeyRoomRateId,
  queryKeyRoomTypeId,
  queryKeyPriceTotal,
  queryKeyUserCountry,
} from 'constants/url'

import { getOfferId } from 'components/Search/SearchResultsMap/offerIdentity'
import { isTodayBeforeCheckin } from 'components/Search/utils'
import type { MonthOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/MonthPicker'
import Line18PlusIcon from 'LuxKit/Icons/line/Line18PlusIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineExhibitionsIcon from 'LuxKit/Icons/line/LineExhibitionsIcon'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import LineGolfIcon from 'LuxKit/Icons/line/LineGolfIcon'
import LineHeartCardIcon from 'LuxKit/Icons/line/LineHeartCardIcon'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import LineHomeAltIcon from 'LuxKit/Icons/line/LineHomeAltIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineHourglassIcon from 'LuxKit/Icons/line/LineHourglassIcon'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import LineLayersAltIcon from 'LuxKit/Icons/line/LineLayersAltIcon'
import LineListUlAltIcon from 'LuxKit/Icons/line/LineListUlAltIcon'
import LineMoneyStackIcon from 'LuxKit/Icons/line/LineMoneyStackIcon'
import LineMountainsSunIcon from 'LuxKit/Icons/line/LineMountainsSunIcon'
import LinePaletteIcon from 'LuxKit/Icons/line/LinePaletteIcon'
import LinePricetagAltIcon from 'LuxKit/Icons/line/LinePricetagAltIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineSchoolBackpackIcon from 'LuxKit/Icons/line/LineSchoolBackpackIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import LineSnowFlakeIcon from 'LuxKit/Icons/line/LineSnowFlakeIcon'
import LineStarCardIcon from 'LuxKit/Icons/line/LineStarCardIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineSunsetIcon from 'LuxKit/Icons/line/LineSunsetIcon'
import LineSwimmerIcon from 'LuxKit/Icons/line/LineSwimmerIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUltraLuxDiamondIcon from 'LuxKit/Icons/line/LineUltraLuxDiamondIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineWaterIcon from 'LuxKit/Icons/line/LineWaterIcon'
import LineWatersportsIcon from 'LuxKit/Icons/line/LineWatersportsIcon'
import LineWellnessIcon from 'LuxKit/Icons/line/LineWellnessIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'

import { sortBy, take, groupBy } from 'lib/array/arrayUtils'
import { sanitiseChildrenAges, serialiseOccupancy } from 'lib/search/occupancy'
import { buildExtractedParamsFromLocation, deleteSearchParams, parseSearchString } from 'lib/url/searchUrlUtils'
import { isLEHotel } from 'lib/offer/offerTypes'
import { isNumber } from 'lib/maths/mathUtils'
import { intlDateFormatter, intlDateRangeFormatter, isSameDay } from 'lib/datetime/dateUtils'
import { isString, slugify } from 'lib/string/stringUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getObjectKey from 'lib/object/getObjectKey'
import type { ParsedQs } from 'qs'
import OfferListFilters = App.OfferListFilters
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { excludeNullOrUndefined } from 'checkout/utils'
import type { OfferPageMultiRoomMode, OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import { FlightsClassTypes, fareClassToUrlMap, type FareClassWithReward } from 'constants/flight'
import uuidV4 from 'lib/string/uuidV4Utils'
import Places from '../../constants/places'
import { getDestinationImage as getDestinationImageFromMap } from './destinationImageMap'
import { getSearchDateSelectionFromExtractedSearchParams } from './typeaheadDateConfirmationUtils'
import { getMaxNonInfantGuestCount } from '../offer/occupancyUtils'
import { EUROPE_REGIONS } from 'constants/config/region'

export function isAnywhereDestinationSearch(destinationId?: string): boolean {
  return !!destinationId?.split(',').includes(ANYWHERE_PLACE_ID)
}

const searchEngineReferrers = ['https://www.google.com/', 'https://www.bing.com/', 'https://au.search.yahoo.com/', 'https://yandex.com/']

function parseSearchItem(
  destinationName: string | undefined,
  destinationId: string | undefined,
  landmarkName: string | undefined,
  landmarkId: string | undefined,
  propertyName: string | undefined,
  propertyId: string | undefined,
  bounds: string | undefined,
  lat: string | undefined,
  lng: string | undefined,
  query: string | undefined,
  extractedParams?: App.ExtractedSearchParamsV2,
  typeaheadId?: string,
  extracting?: boolean,
): App.SearchItem | undefined {
  if (destinationName && destinationId) {
    return {
      searchType: 'destination',
      value: destinationId,
      format: {
        mainText: destinationName,
        secondaryText: '',
      },
    }
  }
  if (landmarkName && landmarkId) {
    return {
      searchType: 'landmark',
      value: landmarkId,
      format: {
        mainText: landmarkName,
        secondaryText: '',
      },
    }
  }
  else if (propertyName && propertyId) {
    return {
      searchType: 'property',
      value: propertyId,
      format: {
        mainText: propertyName,
        secondaryText: '',
      },
    }
  }
  else if (bounds) {
    const intl = getCurrentIntl()
    return {
      searchType: 'bounds',
      value: bounds,
      format: {
        mainText: intl.formatMessage({
          id: '5xq4xjS0C9_gQEdwYNWp',
          defaultMessage: 'Map area',
          description: 'Search item label shown when the user searches by dragging the map to a specific area',
        }),
        secondaryText: '',
      },
    }
  }
  else if (lat && lng) {
    const intl = getCurrentIntl()
    return {
      searchType: 'lat_long',
      value: `${lat},${lng}`,
      format: {
        mainText: intl.formatMessage(
          {
            id: 'ac5f8ixu3ZQ6OODF_-Ts',
            defaultMessage: 'Location ({lat}, {lng})',
            description: 'Search item label showing latitude and longitude coordinates when user searches by location',
          },
          { lat, lng },
        ),
        secondaryText: '',
      },
    }
  }
  else if (query && typeaheadId) {
    return {
      searchType: 'searchItemV3',
      value: query,
      format: {
        mainText: query,
      },
      display: {
        mainText: query,
        searchType: 'naturalLanguage',
      },
      searchItem: {
        typeaheadId,
        extractedData: extractedParams ?? {},
        extracting: extracting ?? false,
      },
    }
  }

  return undefined
}

export interface SearchFilterParams {
  searchItem?: App.SearchItem
  rooms: Array<App.Occupants>
  dates: {
    checkIn?: string
    checkOut?: string
  }
  flexibleSearch: {
    flexibleNights?: string
    flexibleMonths?: string
    userSelectedFlexibleMonths?: boolean
  }
  flexibleWithDate?: string
  bundledOfferId?: string
  customerRatingGte?: string
  offerType?: App.OfferType
  duration?: number
  roomRateId?: string
  roomTypeId?: string
}

// decodeSearchParams returns an object containing search parameters
// by parsing the given URL query parameters
export function decodeSearchParams(searchStr: string, maxChildAge?: number, maxInfantAge?: number, maxTeenagerAge?: number): SearchFilterParams {
  const params = parseSearchString(searchStr)
  const {
    destinationName,
    offerType,
    destinationId,
    landmarkName,
    landmarkId,
    propertyName,
    propertyId,
    bounds,
    lat,
    lng,
    checkIn,
    checkOut,
    daysUntilCheckIn,
    duration,
    flexibleNights,
    flexibleMonths,
    userSelectedFlexibleMonths,
    customerRatingGte,
    roomRateId,
    roomTypeId,
    query,
    semanticRerank,
    typeaheadId,
    extracting,
    amenities,
    inclusions,
    inclusionGap,
    tags,
    chains,
    subChains,
    priceGte,
    priceLte,
    sortBy,
  } = params

  // Parse occupancy once (used for both extractedParams and return value)
  const parsedOccupancy = parseOccupancy(searchStr, maxChildAge, maxInfantAge, maxTeenagerAge)

  // Reconstruct extractedParams from URL params if extraction is complete
  // This stores the extracted data for use elsewhere (e.g., display, API calls)
  // encodeSearchParams preserves these values from URL, so no circular dependency
  const isExtracting = extracting === 'true'
  let extractedParams: App.ExtractedSearchParamsV2 | undefined = undefined

  if (!isExtracting && query) {
    // Build base params
    const baseParams: App.ExtractedSearchParamsBase = {
      query,
      ...(checkIn && { checkIn }),
      ...(checkOut && { checkOut }),
      ...(flexibleNights && { flexibleNights }),
      ...(flexibleMonths && { flexibleMonths }),
      ...(amenities && { amenities: amenities.split(',') }),
      ...(inclusions && { inclusions: inclusions.split(',') }),
      ...(inclusionGap && { inclusionGap }),
      ...(tags && { tags: tags.split(',') }),
      ...(chains && { chains: chains.split(chainsDelimiter) }),
      ...(subChains && { subChains: subChains.split(subChainsDelimiter) }),
      ...(priceGte && { priceGte }),
      ...(priceLte && { priceLte }),
      ...(sortBy === 'value' && { sortBy: 'value' as const }),
      ...(semanticRerank === 'true' && { semanticRerank: true }),
      ...(parsedOccupancy && parsedOccupancy.length > 0 && { occupancy: parsedOccupancy }),
    }

    extractedParams = buildExtractedParamsFromLocation(baseParams, { propertyId, lat, lng, landmarkId, destinationId })
  }

  const parsedSearchItem = parseSearchItem(
    destinationName,
    destinationId,
    landmarkName,
    landmarkId,
    propertyName,
    propertyId,
    bounds,
    lat,
    lng,
    query,
    extractedParams,
    typeaheadId,
    isExtracting,
  )

  const relativeCheckIn = daysUntilCheckIn && moment().add(parseInt(daysUntilCheckIn), 'days').format(ISO_DATE_FORMAT)
  const relativeCheckOut = daysUntilCheckIn && duration && moment().add(parseInt(daysUntilCheckIn) + parseInt(duration), 'days').format(ISO_DATE_FORMAT)

  const parsedDates = {
    checkIn: checkIn || relativeCheckIn || undefined,
    checkOut: checkOut || relativeCheckOut || undefined,
  }
  if (parsedDates.checkIn && !(isTodayBeforeCheckin(parsedDates.checkIn) || isSameDay(new Date(parsedDates.checkIn), new Date()))) {
    parsedDates.checkIn = undefined
    parsedDates.checkOut = undefined
  }

  // if user select travel flexible with date
  const flexibleWithDate = params.flexibleWithDate
  const parsedDuration = Number(duration) || undefined

  return {
    // @ts-ignore parsing a search item doesn't guarantee a search item will be returned, it's assumed it will here
    searchItem: parsedSearchItem,
    rooms: parsedOccupancy,
    dates: (parsedDates.checkIn && parsedDates.checkOut) ? parsedDates : { checkIn: undefined, checkOut: undefined },
    flexibleSearch: {
      flexibleNights,
      flexibleMonths,
      userSelectedFlexibleMonths: userSelectedFlexibleMonths === 'true' ? true : undefined,
    },
    offerType: offerType as App.OfferType,
    customerRatingGte,
    flexibleWithDate,
    bundledOfferId: params.bundledOfferId,
    duration: !(parsedDates.checkIn && parsedDates.checkOut) ? parsedDuration : undefined,
    roomRateId,
    roomTypeId,
  }
}

/**
 * Converts occupancy (App.Occupants) to extractedParams format
 * Used for natural language search extracted params
 */
export function occupancyToExtractedParams(occupancy: App.Occupants): { adults: string; children: string } {
  return {
    adults: occupancy.adults.toString(),
    children: occupancy.childrenAge && occupancy.childrenAge.length > 0
      ? occupancy.childrenAge.join(',')
      : queryNullChildren,
  }
}

export function getOccupancyFromSearchStrings(
  adults: number,
  children: Array<number> = [],
  maxChildAge?: number,
  maxInfantAge?: number,
  maxTeenagerAge?: number,
): App.Occupants {
  const roomOccupancy = {
    adults,
    children: 0,
    infants: 0,
    teenagers: 0,
    childrenAge: [] as Array<number>,
  }

  for (const childAge of children) {
    if (maxChildAge && childAge > maxChildAge) {
      if (maxTeenagerAge && childAge <= maxTeenagerAge) {
        roomOccupancy.teenagers++
        roomOccupancy.childrenAge.push(childAge)
      } else {
        roomOccupancy.adults++
      }
    } else if (childAge > (maxInfantAge ?? 0) && childAge <= (maxChildAge ?? 0)) {
      roomOccupancy.children++
      roomOccupancy.childrenAge.push(childAge)
    } else if (childAge <= (maxInfantAge ?? 0)) {
      roomOccupancy.infants++
      roomOccupancy.childrenAge.push(childAge)
    } else {
      roomOccupancy.children++
      roomOccupancy.childrenAge.push(childAge)
    }
  }

  return roomOccupancy
}

export interface QueryOccupancyResult {
  /** Whether any occupancy params (adults/children/children_ages) were present in the URL. */
  present: boolean
  /** Whether the present params passed validation (positive-integer adults, valid ages, children count matches ages). */
  valid: boolean
  /** The parsed occupancy; only set when present and valid. */
  occupancy?: App.Occupants
}

// getOccupancyFromQueryParams reads the `adults`, `children` and `children_ages` query
// params used by BNBL redemption links (e.g. ?adults=2&children=2&children_ages=0,5) and
// validates them. The link is invalid when an `adults` param is present but not a positive
// integer, when the `children` count does not match the number of `children_ages`, or when
// any child age is not a valid age (a 0..maxTeenagerAge integer). A missing `adults` param is
// not "broken" — it falls back to DEFAULT_BNBL_QUERY_ADULTS. Children ages are categorised
// into children/infants/teenagers using the offer's thresholds.
export function getOccupancyFromQueryParams(
  query: URLSearchParams,
  maxChildAge?: number,
  maxInfantAge?: number,
  maxTeenagerAge?: number,
): QueryOccupancyResult {
  const adultsParam = query.get(queryKeyAdults)
  const childrenParam = query.get(queryKeyChildren)
  const childrenAgesParam = query.get(queryKeyChildrenAges)

  const present = adultsParam != null || childrenParam != null || childrenAgesParam != null
  if (!present) return { present: false, valid: false }

  // Empty/whitespace tokens (e.g. a trailing comma `5,` or `children_ages=%20`) must not coerce
  // to 0 via Number(''); map them to NaN so age validation fails and the link routes to the reset.
  const childrenAges = childrenAgesParam
    ? childrenAgesParam.split(bnblChildrenAgesDelimiter).map(age => {
        const trimmed = age.trim()
        return trimmed === '' ? NaN : Number(trimmed)
      })
    : []

  const agesAreValid = childrenAges.every(age =>
    Number.isInteger(age) && age >= 0 && (maxTeenagerAge == null || age <= maxTeenagerAge),
  )

  const childrenCount = childrenParam != null ? Number(childrenParam) : childrenAges.length
  const countMatchesAges = Number.isInteger(childrenCount) && childrenCount === childrenAges.length

  const adults = Number(adultsParam)
  const adultsAreValid = adultsParam == null || (Number.isInteger(adults) && adults > 0)

  if (!agesAreValid || !countMatchesAges || !adultsAreValid) return { present: true, valid: false }

  const occupancy = getOccupancyFromSearchStrings(
    adultsParam != null ? adults : DEFAULT_BNBL_QUERY_ADULTS,
    childrenAges,
    maxChildAge,
    maxInfantAge,
    maxTeenagerAge,
  )

  return { present: true, valid: true, occupancy }
}

function parseChildrenAges(childrenAgeList: string, delimiter = queryChildrenDelimiter): Array<number> {
  return sanitiseChildrenAges(childrenAgeList.split(delimiter).map(age => parseInt(age, 10)))
}

// buildOccupants constructs occupants array from the given
// encoded adults and children search params
function buildOccupants(
  adults = '',
  children = queryNullChildren,
  maxChildAge?: number,
  maxInfantAge?: number,
  maxTeenagerAge?: number,
): Array<App.Occupants> {
  const allAdults = isNumber(adults) || isString(adults) ? adults.split(',').map(adult => Number(adult)) : []
  const allChildren = isNumber(children) || isString(children) ? children.split(',') : []
  const roomCount = Math.max(allAdults.length, allChildren.length)

  const occupants: Array<App.Occupants> = []

  for (let i = 0; i < roomCount; i++) {
    const childrenAgeList = allChildren[i]

    let children: Array<number> = []
    if (childrenAgeList && childrenAgeList !== queryNullChildren) {
      children = parseChildrenAges(childrenAgeList)
    }

    occupants.push(getOccupancyFromSearchStrings(allAdults[i], children, maxChildAge, maxInfantAge, maxTeenagerAge))
  }

  return occupants
}

// parseOccupancy parses the given adult counts and children age lists and returns an array of
// rooms with adults and children counts
export function parseOccupancy(
  search: string | ParsedQs,
  maxChildAge?: number,
  maxInfantAge?: number,
  maxTeenagerAge?: number,
): Array<App.Occupants> {
  const { adults, children } = parseSearchString(search)
  if (Array.isArray(adults) || Array.isArray(children)) {
    // incorrectly formatted query string, we shouldn't get arrays
    return []
  }

  if (!adults && (!children || children === queryNullChildren)) {
    return []
  }

  return buildOccupants(adults, children, maxChildAge, maxInfantAge, maxTeenagerAge)
}

// parseOccupancyFromParams parses separate adults and children query param strings
// and returns an array of rooms with adults and children counts
export function parseOccupancyFromParams(
  adultsParam: string,
  childrenParam: string,
): Array<App.Occupants> {
  const allAdults = adultsParam.split(',').map(adult => Number(adult))
  const allChildren = childrenParam.split(',')
  const roomCount = Math.max(allAdults.length, allChildren.length)

  const occupants: Array<App.Occupants> = []

  for (let i = 0; i < roomCount; i++) {
    const childrenAgeList = allChildren[i]

    let childrenAges: Array<number> = []
    if (childrenAgeList && childrenAgeList !== queryNullChildren) {
      childrenAges = parseChildrenAges(childrenAgeList)
    }

    occupants.push({
      adults: allAdults[i] || 2,
      children: childrenAges.length,
      infants: 0,
      childrenAge: childrenAges,
    })
  }

  return occupants
}

export function doesOfferPageSearchContextMatch(
  urlSearch: string,
  checkIn: string | undefined,
  checkOut: string | undefined,
  rooms: Array<App.Occupants>,
): boolean {
  const params = new URLSearchParams(urlSearch)
  const urlOccupancy = serialiseOccupancy(parseOccupancy(urlSearch))
  const currentOccupancy = serialiseOccupancy(rooms)

  return (
    params.get(queryKeyCheckIn) === checkIn &&
    params.get(queryKeyCheckOut) === checkOut &&
    urlOccupancy.join('|') === currentOccupancy.join('|')
  )
}

/** Room highlight params when URL stay still matches (keeps rates keys aligned for GHA SSR). */
export function getMetasearchRoomOptionFromSearch(
  urlSearch: string,
  checkIn: string | undefined,
  checkOut: string | undefined,
  rooms: Array<App.Occupants>,
): { roomTypeId: string, roomRateId: string, roomRateGroupId?: string } | undefined {
  if (!doesOfferPageSearchContextMatch(urlSearch, checkIn, checkOut, rooms)) return undefined
  const params = new URLSearchParams(urlSearch)
  const roomTypeId = params.get(queryKeyRoomTypeId) ?? undefined
  // roomRateId = gha-lpc; roomOptionRateId = gha-lpp
  const roomRateId = params.get(queryKeyRoomRateId) || params.get(queryKeyRoomOptionRateId) || undefined
  if (!roomTypeId || !roomRateId) return undefined
  return {
    roomTypeId,
    roomRateId,
    roomRateGroupId: params.get(queryKeyRoomRateGroupId) ?? undefined,
  }
}

/**
 * Deserializes occupancy strings back into an array of Occupants.
 * Each string should be in the format "adults-child1,child2,child3" or just "adults".
 * Example: ["2-4,6,8", "2"] => [{adults: 2, childrenAge: [4,6,8]}, {adults: 2}]
 */
export function deserialiseOccupancy(occupancyStrings: Array<string>): Array<App.Occupants> {
  return take(occupancyStrings, MAX_ROOM_LIMIT).map(occupancyString => {
    const [adultsStr, childrenStr] = occupancyString.split('-')

    const occupant: App.Occupants = {
      adults: Math.min(parseInt(adultsStr, 10) || 2, MAX_ADULTS), // Default to 2 if parsing fails
    }

    if (childrenStr) {
      const childrenAges = parseChildrenAges(childrenStr, ',')

      if (childrenAges.length > 0) {
        occupant.childrenAge = take(childrenAges, MAX_NUM_CHILDREN)
        occupant.children = Math.min(childrenAges.length, MAX_NUM_CHILDREN)
      }
    }

    return occupant
  })
}

// occupancyActive checks if the occupancy has been specified, based on a
// given URL query string
export function occupancyActive(searchStr: string) {
  const params = parseSearchString(searchStr)

  return !!params[queryKeyAdults] && !!params[queryKeyChildren]
}

export const searchQueryParams = [
  queryKeyAdults,
  queryKeyChildren,
  queryKeyDestinationId,
  queryKeyDestinationName,
  queryKeyLandmarkId,
  queryKeyLandmarkName,
  queryKeyPropertyId,
  queryKeyPropertyName,
  queryKeyBounds,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryFlexibleWithDate,
  queryKeyCustomerRatingGte,
  queryKeyFlexibleNights,
  queryKeyFlexibleMonths,
  ...queryKeyFilters,
  queryKeySortBy,
  queryKeyBundledOfferId,
  queryKeyMapPinCount,
  queryKeySelectedOfferIds,
  queryKeyRoomId,
  queryKeyRateId,
  queryKeyMultiRoomMode,
  queryKeyQuery,
  queryKeySemanticRerank,
  queryKeyTypeaheadId,
  queryKeyPriceMode,
  queryKeyTags,
  queryKeyFareClass,
  queryKeyFlights,
  // Metasearch specific query params
  queryKeyRoomRateId,
  queryKeyRoomOptionRateId,
  queryKeyRoomRateGroupId,
  queryKeyRoomTypeId,
  queryKeyPriceTotal,
  queryKeyUserCountry,
]

export function encodeChildrenOccupancy(children?: Array<number>) {
  if (!children || children.length === 0) {
    return queryNullChildren
  }
  const validChildren = sanitiseChildrenAges(children)

  if (!validChildren.length) {
    return queryNullChildren
  }

  return validChildren.join(queryChildrenDelimiter)
}

export function convertDatesToString(checkinDate?: moment.Moment, checkoutDate?: moment.Moment): App.SearchDates {
  const hasDates = !!(checkinDate && checkoutDate)
  const checkIn = hasDates ? checkinDate.format(ISO_DATE_FORMAT) : undefined
  const checkOut = hasDates ? checkoutDate.format(ISO_DATE_FORMAT) : undefined
  return { checkIn, checkOut }
}

export function appendSearchParamValue(searchParams: URLSearchParams, paramName: string, paramValue: string | number) {
  const existingParam = (searchParams.get(paramName) ?? '').split(',').filter(Boolean)
  existingParam.push(paramValue.toString())
  searchParams.set(paramName, existingParam.join(','))
  return searchParams
}

/** Typescript-friendly way to get a property that might exist on a subtype of an object - but only if it's a string */
function getStringProperty<T extends object>(obj: T, property: string): string | undefined {
  const value = (obj as any)[property]
  return typeof value === 'string' ? value : undefined
}

interface EncodeSearchParams {
  urlSearch?: string
  searchItem?: App.SearchItem
  dates?: App.SearchDates
  rooms?: Array<App.Occupants>
  filters?: App.OfferListFilters
  isFlexibleWithDate?: boolean
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  flexibleMonths?: string
  userSelectedFlexibleMonths?: boolean
  sortBy?: OfferListSortOption
  destinationId?: string
  destinationName?: string
  selectedOfferIds?: Array<string>
  multiRoomMode?: OfferPageMultiRoomMode
  fareClass?: FareClassWithReward
  priceMode?: OfferPagePriceMode
  isPriceModeChangedFromDefault?: boolean
  flights?: boolean
  paramsToKeep?: Array<string>
}

// encodeSearchParams encodes the given search parameters into a URL query params
export function encodeSearchParams(params: EncodeSearchParams): URLSearchParams {
  const {
    urlSearch = '',
    searchItem,
    dates,
    rooms,
    filters,
    isFlexibleWithDate,
    flexibleNights,
    flexibleMonths,
    userSelectedFlexibleMonths = false,
    destinationId,
    destinationName,
    selectedOfferIds,
    multiRoomMode,
    fareClass,
    priceMode,
    isPriceModeChangedFromDefault,
    flights,
    paramsToKeep,
  } = params

  // Track if user explicitly provided dates (these take precedence over extracted)
  const hasUserDates = !!(dates?.checkIn && dates.checkOut)
  const hasUserFlexibleDates = !!(flexibleNights && flexibleMonths)

  // clear all search params and a page param
  const searchParams = new URLSearchParams(urlSearch)
  const paramsToRemove = [...searchQueryParams, queryKeyPages].filter(param => !paramsToKeep?.includes(param))
  paramsToRemove.forEach(param => {
    searchParams.delete(param)
  })

  // Always set user-entered dates if provided (they take precedence over extracted)
  if (hasUserDates && dates?.checkIn && dates?.checkOut) {
    searchParams.set(queryKeyCheckIn, dates.checkIn)
    searchParams.set(queryKeyCheckOut, dates.checkOut)
    searchParams.delete(queryKeyDuration)
  }

  if (hasUserFlexibleDates && flexibleNights && flexibleMonths && userSelectedFlexibleMonths !== undefined) {
    searchParams.set(queryKeyFlexibleNights, flexibleNights)
    searchParams.set(queryKeyFlexibleMonths, flexibleMonths)
    searchParams.set(queryKeyUserSelectedFlexibleMonths, userSelectedFlexibleMonths.toString())
  }

  // Set destination if explicitly provided (not from natural language extraction)
  if (destinationId && destinationName) {
    searchParams.set(queryKeyDestinationName, destinationName)
    searchParams.set(queryKeyDestinationId, destinationId)
  }

  // Chear duration if we perform search
  if (searchItem?.searchType) {
    searchParams.delete(queryKeyDuration)
  }

  if (searchItem?.searchType === 'destination') {
    searchParams.set(queryKeyDestinationName, searchItem.format.mainText)
    searchParams.set(queryKeyDestinationId, searchItem.value)
  }
  if (searchItem?.searchType === 'landmark') {
    searchParams.set(queryKeyLandmarkName, searchItem.format.mainText)
    searchParams.set(queryKeyLandmarkId, searchItem.value)
  }
  if (searchItem?.searchType === 'airport') {
    searchParams.set(queryKeyLandmarkName, searchItem.format.mainText)
    searchParams.set(queryKeyLandmarkId, searchItem.value)
  }
  if (searchItem?.searchType === 'property') {
    searchParams.set(queryKeyPropertyName, searchItem.format.mainText)
    searchParams.set(queryKeyPropertyId, searchItem.value)
  }
  if (searchItem?.searchType === 'experience') {
    searchParams.set(queryKeyExperienceName, searchItem.format.mainText)
    searchParams.set(queryKeyExperienceId, searchItem.value)
  }
  if (searchItem?.searchType === 'bounds') {
    searchParams.set(queryKeyBounds, searchItem.value)
  }
  if (searchItem?.searchType === 'lat_long') {
    const [lat, lng] = searchItem.value.split(',')
    searchParams.set(queryKeyLat, lat)
    searchParams.set(queryKeyLng, lng)
  }
  if (searchItem?.searchType === 'searchItemV3' && searchItem?.display?.searchType === 'naturalLanguage') {
    const { extractedData, extracting, typeaheadId } = searchItem.searchItem

    // If extraction is complete and extractedData exist, encode them to URL params
    if (!extracting && extractedData) {
      const { searchType } = extractedData

      // Set location params from extracted data based on searchType
      if (searchType === 'property' && extractedData.property) {
        searchParams.set(queryKeyPropertyId, extractedData.property.propertyId)
      } else if (searchType === 'latLong' && extractedData.lat && extractedData.lng) {
        searchParams.set(queryKeyLat, extractedData.lat)
        searchParams.set(queryKeyLng, extractedData.lng)
      } else if (searchType === 'landmark' && extractedData.placeIds && extractedData.placeIds.length > 0) {
        searchParams.set(queryKeyLandmarkId, extractedData.placeIds.join(','))
      } else if (searchType === 'destination' && extractedData.placeIds && extractedData.placeIds.length > 0) {
        searchParams.set(queryKeyDestinationId, extractedData.placeIds.join(','))
      }

      const extractedDateSelection = getSearchDateSelectionFromExtractedSearchParams(extractedData)
      // Form/URL dates from the search bar win over stale extractedData once the
      // user has chosen dates (including edits made during confirmation review).
      if (!hasUserDates && !hasUserFlexibleDates) {
        if (extractedDateSelection?.type === 'specific') {
          searchParams.set(queryKeyCheckIn, extractedDateSelection.checkIn)
          searchParams.set(queryKeyCheckOut, extractedDateSelection.checkOut)
          searchParams.delete(queryKeyDuration)
          searchParams.delete(queryKeyFlexibleNights)
          searchParams.delete(queryKeyFlexibleMonths)
        } else if (extractedDateSelection?.type === 'flexible') {
          searchParams.set(queryKeyFlexibleNights, extractedDateSelection.flexibleNights)
          searchParams.set(queryKeyFlexibleMonths, extractedDateSelection.flexibleMonths)
          searchParams.delete(queryKeyCheckIn)
          searchParams.delete(queryKeyCheckOut)
        }
      }

      // Set filters from extracted data
      if (extractedData.amenities && extractedData.amenities.length > 0) {
        searchParams.set(queryKeyAmenities, extractedData.amenities.join(','))
      }
      if (extractedData.inclusions && extractedData.inclusions.length > 0) {
        searchParams.set(queryKeyInclusions, extractedData.inclusions.join(','))
      }
      if (extractedData.inclusionGap) {
        searchParams.set(queryKeyInclusionGap, extractedData.inclusionGap)
      }
      if (extractedData.tags && extractedData.tags.length > 0) {
        searchParams.set(queryKeyTags, extractedData.tags.join(','))
      }
      if (extractedData.chains && extractedData.chains.length > 0) {
        searchParams.set(queryKeyChains, extractedData.chains.join(chainsDelimiter))
      }
      if (extractedData.subChains && extractedData.subChains.length > 0) {
        searchParams.set(queryKeySubChains, extractedData.subChains.join(subChainsDelimiter))
      }
      if (extractedData.priceGte) {
        searchParams.set(queryKeyPriceGte, extractedData.priceGte)
      }
      if (extractedData.priceLte) {
        searchParams.set(queryKeyPriceLte, extractedData.priceLte)
      }
      if (extractedData.sortBy) {
        searchParams.set(queryKeySortBy, extractedData.sortBy)
      }
      if (extractedData.semanticRerank === true) {
        searchParams.set(queryKeySemanticRerank, 'true')
      }

      searchParams.set(queryKeyQuery, searchItem.value)
    } else {
      // Extraction in progress or no extracted data - use standard query params
      searchParams.set(queryKeyQuery, searchItem.value)

      // Add extracting=true only if extraction is in progress
      if (extracting) {
        searchParams.set(queryKeyExtracting, 'true')
      }
    }

    // Always preserve typeaheadId if it exists on the searchItem (for tracking purposes)
    if (typeaheadId) {
      searchParams.set(queryKeyTypeaheadId, typeaheadId)
    }
  }
  // Handle SearchItemV3 where display.searchType is NOT naturalLanguage
  if (searchItem?.searchType === 'searchItemV3' && searchItem?.display?.searchType !== 'naturalLanguage') {
    const { extractedData, typeaheadId } = searchItem.searchItem
    const { searchType } = extractedData

    // Use query param for the display text (enables URL reconstruction with typeaheadId)
    searchParams.set(queryKeyQuery, searchItem.value)

    // Set location ID params based on extractedData.searchType
    if (searchType === 'property' && extractedData.property) {
      searchParams.set(queryKeyPropertyId, extractedData.property.propertyId)
    } else if (searchType === 'latLong' && extractedData.lat && extractedData.lng) {
      searchParams.set(queryKeyLat, extractedData.lat)
      searchParams.set(queryKeyLng, extractedData.lng)
    } else if (searchType === 'landmark' && extractedData.placeIds && extractedData.placeIds.length > 0) {
      searchParams.set(queryKeyLandmarkId, extractedData.placeIds.join(','))
    } else if (searchType === 'destination' && extractedData.placeIds && extractedData.placeIds.length > 0) {
      searchParams.set(queryKeyDestinationId, extractedData.placeIds.join(','))
    }

    // Preserve typeaheadId for tracking and URL reconstruction
    if (typeaheadId) {
      searchParams.set(queryKeyTypeaheadId, typeaheadId)
    }
  }

  if (selectedOfferIds && selectedOfferIds.length > 0) {
    searchParams.set(queryKeySelectedOfferIds, selectedOfferIds.join(','))
  }

  if (rooms && rooms.length > 0) {
    searchParams.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    searchParams.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    if (multiRoomMode === 'individual') {
      if (rooms.some(room => getStringProperty(room, 'roomId'))) {
        searchParams.set(queryKeyRoomId, rooms.map(room => getStringProperty(room, 'roomId') ?? '').join(','))
      }
      if (rooms.some(room => getStringProperty(room, 'rateId'))) {
        searchParams.set(queryKeyRateId, rooms.map(room => getStringProperty(room, 'rateId') ?? '').join(','))
      }
    }

    if (multiRoomMode && rooms.length > 1) {
      searchParams.set(queryKeyMultiRoomMode, multiRoomMode)
    }
  }

  if (fareClass && fareClass !== FlightsClassTypes.ECONOMY) {
    searchParams.set(queryKeyFareClass, fareClassToUrlMap[fareClass])
  }

  if (filters?.propertyTypes?.length) {
    searchParams.set(queryKeyPropertyTypes, filters.propertyTypes.join(','))
  }

  if (filters?.campaigns?.length) {
    searchParams.set(queryKeyCampaigns, filters.campaigns.join(','))
  }

  if (filters?.offerTypes?.length) {
    searchParams.set(queryKeyUrlOfferType, filters.offerTypes.join(','))
  }

  if (filters?.holidayTypesScoped?.length) {
    searchParams.set(queryKeyHolidayTypesScoped, filters?.holidayTypesScoped?.join(','))
  }

  if (filters?.holidayTypes?.length) {
    searchParams.set(queryKeyHolidayTypes, filters.holidayTypes.join(','))
  }

  if (filters?.customerRatingGte) {
    searchParams.set(queryKeyCustomerRatingGte, filters.customerRatingGte.toString())
  }

  if (filters?.amenities?.length) {
    searchParams.set(queryKeyAmenities, filters.amenities.join(','))
  }

  if (filters?.inclusions?.length) {
    searchParams.set(queryKeyInclusions, filters.inclusions.join(','))
  }

  if (filters?.inclusionGap) {
    searchParams.set(queryKeyInclusionGap, filters.inclusionGap)
  }

  if (isFlexibleWithDate === Boolean(true)) {
    searchParams.set(queryFlexibleWithDate, isFlexibleWithDate.toString())
  }

  if (filters?.sortBy) {
    searchParams.set(queryKeySortBy, filters.sortBy)
  }

  if (filters?.priceLte) {
    searchParams.set('priceLte', filters.priceLte.toString())
  }

  if (filters?.priceGte) {
    searchParams.set('priceGte', filters.priceGte.toString())
  }

  if (filters?.luxPlusFeatures?.length) {
    searchParams.set('luxPlusFeatures', filters.luxPlusFeatures.join(','))
  }

  if (filters?.locations?.length) {
    searchParams.set('locations', filters.locations.join(','))
  }

  if (filters?.isLuxPlus) {
    searchParams.set('isLuxPlus', filters.isLuxPlus.toString())
  }

  if (filters?.loyalty?.length) {
    searchParams.set('loyalty', filters.loyalty.join(','))
  }

  if (filters?.categories?.length) {
    searchParams.set('categories', filters.categories.join(','))
  }

  if (priceMode && isPriceModeChangedFromDefault) {
    searchParams.set(queryKeyPriceMode, priceMode)
  }

  if (flights) {
    searchParams.set(queryKeyFlights, 'true')
  }

  return searchParams
}

// Removes the search parameters from the URL
export function removeSearchParamsFromURL(search: string) {
  return deleteSearchParams(search, ...searchQueryParams, queryKeyPages)
}

// Returns true if user didn't select children age
export function isRoomsInvalid(rooms: Array<App.Occupants>) {
  return rooms.some(room =>
    // sometimes childrenAge isn't defined on older occupants objects
    (room.childrenAge ?? []).some(child =>
      child === -1,
    ),
  )
}

export function isSingleRoomInvalid(guestCounts: App.Occupants) {
  return guestCounts.childrenAge?.some(child => child === -1)
}

export function buildSearchParamsKey(
  checkIn: string = '',
  checkOut: string = '',
  rooms: Array<App.Occupants> = [],
  bundledOfferId?: string,
) {
  return `${checkIn}-${checkOut}-${serialiseOccupancy(rooms).join(',')}${bundledOfferId ? `-${bundledOfferId}` : ''}`
}

export function buildSuggestedDatesParamsKey(
  flexibleMonths: string = '',
  flexibleNights: string = '',
  rooms: Array<App.Occupants> = [],
) {
  return `${flexibleMonths}-${flexibleNights}-${serialiseOccupancy(rooms).join(',')}`
}

export function buildDestinationSearchParamsKey(
  destinationId: string,
  checkIn: string = '',
  checkOut: string = '',
  rooms: Array<App.Occupants> = [],
) {
  return `${destinationId}|${checkIn}|${checkOut}|${serialiseOccupancy(rooms).join(',')}`
}

export function buildSearchParamsFromFilters(filters: App.OfferListFilters = {}, suggestedDates?: App.OfferSuggestedDates, opts: {
  noDatesInUrl?: boolean
} = { noDatesInUrl: false }) {
  const params = new URLSearchParams()

  if (suggestedDates?.checkIn && suggestedDates?.checkOut) {
    params.set(queryKeyCheckIn, suggestedDates.checkIn)
    params.set(queryKeyCheckOut, suggestedDates.checkOut)
  } else if (filters.checkIn && filters.checkOut) {
    params.set(queryKeyCheckIn, filters.checkIn)
    params.set(queryKeyCheckOut, filters.checkOut)
  }

  if (opts.noDatesInUrl) {
    params.delete(queryKeyCheckIn)
    params.delete(queryKeyCheckOut)
  }

  if (filters.rooms) {
    params.set(queryKeyAdults, filters.rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, filters.rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))
  }

  if (filters.roomRateId && filters.roomTypeId) {
    params.set('roomRateId', filters.roomRateId)
    params.set('roomTypeId', filters.roomTypeId)
    if (filters.roomRateGroupId) {
      params.set('roomRateGroupId', filters.roomRateGroupId)
    }
  }

  if (filters.fareClass) {
    params.set('fareClass', fareClassToUrlMap[filters.fareClass as FlightsClassTypes])
  }
  if (filters.departureAirportCode) {
    params.set(queryKeyDepartureAirport, filters.departureAirportCode)
  }

  return params
}

export function buildSuggestedDatesString(checkIn: string, checkOut: string): string {
  const formattedCheckIn = moment(checkIn)
  const formattedCheckOut = moment(checkOut)
  const nights = formattedCheckOut.diff(formattedCheckIn, 'days')
  const intl = getCurrentIntl()
  const locale = intl.locale
  return intl.formatMessage({
    id: 'nS0Ansp3NyiEwv-yNzPu',
    defaultMessage: '{checkIn} - {checkOut} ({count, plural, one {# night} other {# nights}})',
    description: 'Suggested dates string with check-in, check-out and night count',
  }, {
    checkIn: intlDateFormatter(formattedCheckIn, INTL_DAY_MONTH_NAME, locale),
    checkOut: intlDateFormatter(formattedCheckOut, INTL_DAY_MONTH_NAME, locale),
    count: nights,
  })
}

export function buildSearchStringFromFilters(filters: App.OfferListFilters = {}) {
  return buildSearchParamsFromFilters(filters).toString()
}

export function isSearchEnabled() {
  return config.SEARCH_ENABLED
}

export function isMobileSearchEnabled() {
  return config.MOBILE_SEARCH_ENABLED
}

export function getSearchTypeFromFilters(filters: App.OfferListFilters): App.SearchItem['searchType'] {
  if (filters.landmarkId) { return 'landmark' }
  else if (filters.propertyId) { return 'property' }
  else if (filters.bounds) { return 'bounds' }
  else { return 'destination' }
}

export const getNextNMonths = (options?: {
  start?: moment.Moment
  monthsRequired?: number
}): Array<MonthOption> => {
  const monthsRequired = options?.monthsRequired ?? 12
  const startDate = options?.start ?? moment()
  const locale = getCurrentIntl().locale
  const monthOptions: Array<MonthOption> = []

  for (let i = 0; i < monthsRequired; i++) {
    const current = startDate.clone().add(i, 'months')
    const monthText = intlDateFormatter(current, INTL_LONG_MONTH, locale)
    const monthNumber = current.format('MM')
    const year = current.format('YYYY')

    monthOptions.push({
      monthText,
      monthNumber,
      year,
    })
  }

  return monthOptions
}

export function addFlexibleMonthsIfNoneSelected(flexibleNights?: FLEXIBLE_DURATION_RANGE, flexibleMonths?: string) {
  if (!flexibleNights || flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION || flexibleMonths) {
    return flexibleMonths
  }
  const monthOptions = getNextNMonths({ start: moment().add(4, 'months'), monthsRequired: 2 }).map(month => `${month.year}-${month.monthNumber}`)
  return monthOptions.toString()
}

interface DateRange {
  start: Moment
  end: Moment
}

export function convertFlexibleMonthsToDateRange(flexibleMonths: string): Array<DateRange> {
  return flexibleMonths.split(',').map((month) => {
    const numDays = moment(month, ISO_DATE_SHORT_FORMAT).daysInMonth()
    if (!numDays) {
      return
    }

    const start = moment(`${month}-01`)
    const end = moment(`${month}-${numDays}`)

    return { start, end }
  }).filter((value): value is DateRange => value !== undefined)
}

export function getMinimumDurationFromFlexibleNights(flexibleNights: FLEXIBLE_DURATION_RANGE): number {
  if (flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION) return 4

  const [lowestNightsInRange] = flexibleNights.split('-')
  return lowestNightsInRange ? Number(lowestNightsInRange) : 1
}

export function getFlexibleNightRange(nightDuration?: FLEXIBLE_DURATION_RANGE): [number | undefined, number | undefined] {
  // any duration doesn't have numbers, even though they're specified in the enum
  if (nightDuration && nightDuration !== DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION) {
    const values = nightDuration.split('-')
    return [parseInt(values[0]), parseInt(values[1])]
  }

  return [undefined, undefined]
}

export function convertFlexibleDatesToTravelDates(flexibleNights: FLEXIBLE_DURATION_RANGE, flexibleMonths: string): { checkIn: string, checkOut: string } {
  const duration = getMinimumDurationFromFlexibleNights(flexibleNights)

  const dateRanges = convertFlexibleMonthsToDateRange(flexibleMonths)
  const now = moment()

  for (const dateRange of dateRanges) {
    const { end } = dateRange
    if (now.clone().add(duration, 'days').isAfter(end)) {
      continue
    }
    const checkIn = end.clone().subtract(duration, 'days').format(ISO_DATE_FORMAT)
    const checkOut = end.format(ISO_DATE_FORMAT)
    return {
      checkIn,
      checkOut,
    }
  }

  const checkIn = now.add(1, 'day').format(ISO_DATE_FORMAT)
  const checkOut = now.add(duration, 'days').format(ISO_DATE_FORMAT)

  return {
    checkIn,
    checkOut,
  }
}

export const HolidayTypeToIconMap: Record<string, React.ReactElement> = {
  all: <LineLayersAltIcon />,
  apartments: <LineBuildingIcon />,
  arts_culture: <LinePaletteIcon />,
  adults_only: <Line18PlusIcon />,
  all_inclusive: <LineCheckCircleIcon />,
  boutique: <LineExhibitionsIcon />,
  bucket_list: <LineListUlAltIcon />,
  city_break: <LineBuildingIcon />,
  cruises: <LineShipIcon />,
  dining_drinks: <LineRestaurantIcon />,
  dive: <LineSwimmerIcon />,
  experiences: <LineHotAirBalloonIcon />,
  family_friendly: <LineKidIcon />,
  food_wine: <LineRestaurantIcon />,
  glamping: <LineMountainsSunIcon />,
  golf: <LineGolfIcon />,
  group: <LineUsersAltIcon />,
  guest_house: <LineHomeAltIcon />,
  hill_stations: <LineSightseeingIcon />,
  homes_apartments: <LineHomeAltIcon />,
  homes_villas: <LineHomeAltIcon />,
  honeymoon: <LineHeartIcon />,
  islands: <LineWatersportsIcon />,
  le_fave: <LineStarIcon />,
  limited_time_lux_exclusive: <LineStarCardIcon />,
  limited_time_exclusive: <LineStarCardIcon />,
  lodge: <LineMountainsSunIcon />,
  multi_unit: <LineHomeAltIcon />,
  outdoor_adventure: <LineMountainsSunIcon />,
  relaxed: <LineBedIcon />,
  riad: <LineExhibitionsIcon />,
  romantic: <LineHeartCardIcon />,
  ryokan: <LineExhibitionsIcon />,
  school_holiday: <LineSchoolBackpackIcon />,
  short_stay: <LineClockIcon />,
  single_unit: <LineHomeAltIcon />,
  ski_snow: <LineSnowFlakeIcon />,
  solo_traveler: <LineUserIcon />,
  solo_traveller: <LineUserIcon />,
  spa_break: <LineWellnessIcon />,
  sun_beach: <LineSunsetIcon />,
  sustainable_travel: <LineTreesIcon />,
  tours: <LineCompassIcon />,
  ultra_lux: <LineUltraLuxDiamondIcon />,
  unique_escapes: <LineStarIcon />,
  unique_stays: <LineStarIcon />,
  wellness_relaxation: <LineWellnessIcon />,
  trending: <LineFireIcon />,
  ending_soon: <LineHourglassIcon />,
  latest_new_offer: <LinePricetagAltIcon />,
  best_value: <LineMoneyStackIcon />,
  private_pool: <LineSwimmerIcon />,
  ocean_views: <LineWaterIcon />,
}

export function getHolidayTypeIcon(value: string): React.ReactElement {
  return HolidayTypeToIconMap[value] ?? HolidayTypeToIconMap[slugify(value)] ?? <LineCheckIcon />
}

// [slug, count, order, label]
export type GetTopNFilterItem = [string, number, number, string]

export function getTopNFilterOptions(
  filters: Array<App.FilterItem> | undefined,
  orders: Record<string, number> = {},
  topN: number,
  exclude: Array<string> = [],
): Array<GetTopNFilterItem> | undefined {
  if (!filters) {
    return undefined
  }

  const options: Array<GetTopNFilterItem> = filters
    .filter(item => !exclude.includes(item.slug.toLowerCase()))
    .map(item => [item.slug, item.count, orders[item.slug] ?? 0, item.label])

  return take(sortBy(options, (option) => option[2], 'desc'), topN)
}

export function getQueryParams(search: string, optionValue: string, optionText: string, filters?: App.OfferListFilters): string {
  const searchItem: App.SearchItem = {
    searchType: 'destination',
    value: optionValue,
    format: {
      mainText: optionText,
    },
  }
  const queryParams = encodeSearchParams({
    urlSearch: search,
    searchItem,
    rooms: [config.search.defaultOccupants],
    filters,
    isFlexibleWithDate: true,
  }).toString()

  return queryParams
}

export function isOrganicSearch(referrer: string): boolean {
  return searchEngineReferrers.includes(referrer) || referrer.startsWith('https://www.baidu.com/')
}

export function encodeOfferIds(offers: Array<App.AnyOffer | App.OfferListMetaData>) {
  return offers
    .map(offer => {
      const offerId = getOfferId(offer)
      return offer.type === OFFER_TYPE_BED_BANK ? `bedbank:${offerId}` : `le:${offerId}`
    })
    .join(',')
}

export type EncodedOfferId = {
  kind: 'le' | 'bedbank'
  id: string
}

export function decodeOfferIds(idsParam?: string): Array<EncodedOfferId> {
  if (!idsParam) return []

  return idsParam.split(',').map(idWithKind => {
    const [kind, id] = idWithKind.split(':')
    if (!id) {
      return { kind: 'le', id: kind }
    }

    return {
      kind: kind === 'bedbank' ? 'bedbank' : 'le',
      id,
    }
  })
}

/**
 * Constructs a search query params based on the props found in the offer list filter object
 * Note: Will ignore offer types (assumes sending to the correct search page instead)
 *
 * @param filters An offer list filter to generate the query from
 */
export function getSearchQueryFromFilter(filters: App.OfferListFilters) {
  return encodeSearchParams({
    rooms: filters.rooms ?? [config.search.defaultOccupants],
    dates: {
      checkIn: filters.checkIn,
      checkOut: filters.checkOut,
    },
    flexibleMonths: filters.flexibleMonths,
    ...(filters.flexibleNights && { flexibleNights: filters.flexibleNights as FLEXIBLE_DURATION_RANGE }),
    destinationId: filters.destinationId ?? ANYWHERE_PLACE_ID,
    destinationName: filters.destinationName ?? ANYWHERE_NAME,
    filters: {
      ...filters,
      offerTypes: undefined,
    },
  })
}

export function getCenterCoordinates([lat1, lng1, lat2, lng2]: [number, number, number, number]) {
  const centerLat = (lat1 + lat2) / 2
  const centerLng = (lng1 + lng2) / 2
  return [centerLat, centerLng]
}

export function getSearchVerticalFromPathname(pathname: string): App.SearchVertical | undefined {
  if (pathname.endsWith('/search')) {
    return 'hotels'
  }

  if (pathname.includes('/search/tours')) {
    return 'tours'
  }

  if (pathname.includes('/search/cruises')) {
    return 'cruises'
  }

  if (pathname.includes('/search/experiences')) {
    return 'experiences'
  }

  if (pathname.includes('/search/car-hire')) {
    return 'car-hire'
  }

  // Allow the filter and map work for destination landing page
  if (pathname.includes('/holiday-packages') || pathname.includes('/resorts')) {
    return 'hotels'
  }
}

export function createRecentPropertySearchObject(
  searchItem: App.SearchItem,
  matchingOffer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary,
): App.SearchProperty {
  return {
    searchType: 'property',
    offerId: isLEHotel(matchingOffer) ? matchingOffer.id : undefined,
    value: searchItem.value,
    format: {
      ...searchItem.format,
      imageId: matchingOffer.image.id,
    },
    imageId: matchingOffer.image.id,
  }
}

export function getDestinationImage(destinationId: string): string | undefined {
  return getDestinationImageFromMap(destinationId)
}

export function isDestinationSearchItem(searchItem: App.SearchItem): searchItem is App.SearchDestination {
  return (
    searchItem.searchType === 'destination'
  )
}

/**
 * Maps SearchItemV3's display.searchType to legacy search types.
 * Used for analytics, URL encoding, and other places that need a consistent search type.
 *
 * Mapping:
 * - property → 'property'
 * - destination, city, province, state, address → 'destination'
 * - landmark, airport, train_station → 'landmark'
 * - latLong → 'lat_long'
 * - naturalLanguage → 'naturalLanguage'
 */
export function mapSearchItemV3TypeToLegacyType(
  displaySearchType: App.SearchItemV3['display']['searchType'],
): App.SearchType {
  switch (displaySearchType) {
    case 'property':
      return 'property'
    case 'destination':
    case 'city':
    case 'province':
    case 'state':
    case 'address':
      return 'destination'
    case 'landmark':
    case 'airport':
    case 'train_station':
    case 'metro_station':
      return 'landmark'
    case 'latLong':
      return 'lat_long'
    case 'naturalLanguage':
      return 'naturalLanguage' as App.SearchType
    default:
      return 'destination'
  }
}

/**
 * Gets the effective search type from any SearchItem.
 * For SearchItemV3, maps display.searchType to legacy types.
 * For V1 types, returns searchType directly.
 */
export function getEffectiveSearchType(searchItem: App.SearchItem | undefined): App.SearchType | undefined {
  if (!searchItem) return undefined

  if (searchItem.searchType === 'searchItemV3') {
    return mapSearchItemV3TypeToLegacyType(searchItem.display.searchType)
  }

  return searchItem.searchType
}

export function getDistanceEqFilter(searchType: App.SearchItem['searchType']): number | undefined {
  return searchType === 'destination' ? 0 : undefined
}

export function getDistanceLteFilter(searchType: App.SearchItem['searchType']): number | undefined {
  return searchType === 'landmark' || searchType === 'airport' || searchType === 'lat_long' ? 3000 : undefined
}

interface ScheduleVisibilitiesFilterOptions {
  isSpoofed?: boolean
  isLuxPlusEnabled?: boolean
  canViewLuxPlusBenefits?: boolean
  existingScheduleVisibilities?: App.OfferListFilters['scheduleVisibilities']
}

/**
 * The boolean overload is for legacy spoof-only callers that should not include
 * Lux Plus visibilities. Lux Plus-aware callers should pass the options object.
 */
export function getsScheduleVisibilitiesFilter(
  isSpoofedOrOptions: boolean | ScheduleVisibilitiesFilterOptions,
): NonNullable<App.OfferListFilters['scheduleVisibilities']> {
  const options = typeof isSpoofedOrOptions === 'boolean' ? { isSpoofed: isSpoofedOrOptions } : isSpoofedOrOptions
  const scheduleVisibilities = new Set(options.existingScheduleVisibilities)

  scheduleVisibilities.add('listVisible')

  if (options.isSpoofed) {
    scheduleVisibilities.add('spoofVisible')
  }

  if (options.isLuxPlusEnabled) {
    scheduleVisibilities.add('luxPlusEarlyAccessVisible')
  }

  if (options.canViewLuxPlusBenefits) {
    scheduleVisibilities.add('luxPlusExclusiveVisible')
  }

  return Array.from(scheduleVisibilities)
}

interface GenerateSearchStringParams {
  windowSearch: string
  searchItem: App.SearchItem | undefined
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
  filters?: App.OfferListFilters
  hasLocationChanged: boolean
  isFlexibleWithDate?: boolean
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  flexibleMonths?: string
  userSelectedFlexibleMonths?: boolean
}

export function generateSearchString({
  windowSearch,
  searchItem,
  checkIn,
  checkOut,
  rooms,
  filters,
  hasLocationChanged,
  isFlexibleWithDate,
  flexibleNights,
  flexibleMonths,
  userSelectedFlexibleMonths,
}: GenerateSearchStringParams): string {
  const dates = (checkIn && checkOut) ? { checkIn, checkOut } : undefined
  const flexibleMonthsToSearch = addFlexibleMonthsIfNoneSelected(flexibleNights, flexibleMonths)
  const filtersToApply = hasLocationChanged && searchItem?.searchType !== 'bounds' ? undefined : filters

  const urlSearchParams = encodeSearchParams({
    urlSearch: windowSearch,
    searchItem,
    dates,
    rooms,
    filters: filtersToApply,
    isFlexibleWithDate,
    flexibleNights,
    flexibleMonths: flexibleMonthsToSearch,
    userSelectedFlexibleMonths,
  })

  return urlSearchParams.toString()
}

export function getSearchKey(params: Record<string, any>): string {
  const { pages, ...rest } = params
  return getObjectKey(rest)
}

export function formatDateRangesForAlternativeDates(checkIn: string, checkOut: string): string {
  return intlDateRangeFormatter(
    checkIn,
    checkOut,
    INTL_DAY_MONTH_NAME_SHORT,
    getCurrentIntl().locale,
  )
}

export function getOfferSearchMapUrl(
  offer: App.AnyOffer,
  additionalParams?: URLSearchParams,
): string {
  const params = additionalParams ?? new URLSearchParams()
  params.set(queryKeySelectedOfferIds, encodeOfferIds([offer]))
  return `/search/map?${params.toString()}`
}

export function mapSearchContextToSchema(searchContext: App.SearchContext): comluxgroup.SearchContext_1_1_1 {
  return {
    vertical: searchContext.vertical,
    searchId: searchContext.searchId,
    destinationType: searchContext.destinationType ?? undefined,
    destinations: searchContext.destinations ?? undefined,
    destinationIds: searchContext.destinationIds ?? undefined,
    landmarkIds: searchContext.landmarkIds ?? undefined,
    propertyIds: searchContext.propertyIds ?? undefined,
    origins: searchContext.origins ?? undefined,
    originIds: searchContext.originIds ?? undefined,
    dateType: searchContext.dateType ?? undefined,
    startDate: searchContext.startDate ?? undefined,
    endDate: searchContext.endDate ?? undefined,
    flexibleMonths: searchContext.flexibleMonths ?? undefined,
    durationType: searchContext.durationType ?? undefined,
    flightType: searchContext.flightType ?? undefined,
    adults: searchContext.adults ?? undefined,
    children: searchContext.children ?? undefined,
    childrenAges: searchContext.childrenAges ?? undefined,
    rooms: searchContext.rooms ?? undefined,
    occupancy: searchContext.occupancy?.map(room => ({
      adults: room.adults ?? null,
      ...(room.childrenAges?.length ? { childrenAges: room.childrenAges } : {}),
    })) ?? undefined,
    groupType: searchContext.groupType ?? undefined,
    cruiseLines: searchContext.cruiseLines ?? undefined,
    driverAge: searchContext.driverAge ?? undefined,
    pickUpTime: searchContext.pickUpTime ?? undefined,
    dropOffTime: searchContext.dropOffTime ?? undefined,
    experienceType: searchContext.experienceType ?? undefined,
    flightClass: searchContext.flightClass ?? undefined,
    naturalLanguageQuery: searchContext.query ?? undefined,
  }
}

export function getShouldUseSearchPrices(filters: OfferListFilters | undefined, isMetaSearch?: boolean): boolean {
  if (
    !filters ||
    (filters?.roomRateId && filters?.roomTypeId) ||
    !!filters.oneClickBookingRestoration ||
    (isMetaSearch && !filters.checkIn && !filters.checkOut)
  ) return false

  return isHotelSpecificRequest({ filters })
}

export function calculateOfferTileDiscountPercentage({
  price,
  value,
}: {
  price: number
  value: number
}): number {
  return getValueOffPercent(value, price) / 100
}

/**
 * Determines whether we can use a new endpoint in search for getting an offer list
 * for hotels. This is a faster end point and should be used where possible.
 * @param params The filter list params to determine if we can use it
 * @returns Whether or not we can use the end point
 */
export function isHotelSpecificRequest({ filters }: { filters: App.OfferListFilters | undefined }): boolean {
  const supportedHotelRequestTypes: Array<App.OfferListOfferType> = [
    OFFER_TYPE_HOTEL,
    OFFER_TYPE_ALWAYS_ON,
    OFFER_TYPE_BED_BANK,
    OFFER_TYPE_BUNDLE_AND_SAVE,
    OFFER_TYPE_VILLA,
  ]

  return !!filters &&
    !!(filters.offerTypes?.every(type => supportedHotelRequestTypes.includes(type))) &&
    filters.sortBy !== 'vertical-hotel' &&
    filters.sortBy !== 'carousel-hotel' &&
    filters.sortBy !== 'vertical-ultralux' &&
    filters.sortBy !== 'home' &&
    !filters.useUnifiedSearchEndpoint
}

export function isStandardOccupancy(filters: App.OfferListFilters | undefined): boolean {
  return filters?.rooms?.length === 1 &&
    filters.rooms[0].adults <= 2 &&
    (!filters.rooms[0].children || filters.rooms[0].children === 0) &&
    (!filters.rooms[0].infants || filters.rooms[0].infants === 0)
}

export interface RoomDetails {
  name: string
  size?: string
  bedConfiguration: string | undefined
  totalGuests?: number
  numberOfBedrooms?: number
  numberOfBathrooms?: number
}

export function mapSearchOccupancyToOccupants(occupancy: App.SearchOccupancy): App.Occupants {
  return {
    adults: occupancy.numberOfAdults,
    children: occupancy.agesOfChildren.length,
    childrenAge: occupancy.agesOfChildren,
  }
}

export function getRoomBreakdown(
  rooms: Array<App.ReservationSearchRoom>,
  packages: Array<App.HotelPackage>,
): Array<RoomDetails> {
  const filteredRooms = rooms.filter(r => r.packageId || r.roomRateId)
  const roomsByIdMap = groupBy(
    filteredRooms,
    (room): string => room.packageId || room.roomRateId || '',
  )

  // Transform grouped rooms into display format with count
  return Array.from(roomsByIdMap.values())
    .map(roomList => {
      const count = roomList.length
      const room = roomList[0]

      if (!room) return undefined

      // Get room details based on whether the first room has packageId or roomRateId
      const roomDetails = room.packageId
        ? getRoomDetailsByPackageId(room.packageId, packages)
        : getRoomDetailsByRoomRateId(room.roomRateId, packages)

      if (!roomDetails) return undefined

      return {
        ...roomDetails,
        name: `${count}x ${roomDetails.name}`,
        totalGuests: roomDetails.totalGuests,
        numberOfBedrooms: roomDetails.numberOfBedrooms,
        numberOfBathrooms: roomDetails.numberOfBathrooms,
      }
    })
    .filter(excludeNullOrUndefined)
}

function getRoomDetailsByPackageId(
  packageId: string,
  packages: Array<App.HotelPackage>,
): RoomDetails | undefined {
  const pkg = packages.find(p => p.id === packageId)
  const roomType = pkg?.roomType

  if (!roomType?.name || !roomType.sizeSqm) return undefined

  const roomDetails = {
    name: roomType.name,
    size: `${roomType.sizeSqm}m²`,
    bedConfiguration: undefined,
    totalGuests: getMaxNonInfantGuestCount(pkg?.roomRate?.capacities ?? []),
    numberOfBedrooms: roomType.numberOfBedrooms,
    numberOfBathrooms: roomType.numberOfBathrooms,
  }

  if (!roomType.bedGroups?.length) return roomDetails

  return {
    ...roomDetails,
    bedConfiguration: roomType.bedGroups
      .map(bedGroup => bedGroup.description)
      .join(' or '),
  }
}

function getRoomDetailsByRoomRateId(
  roomRateId: string,
  packages: Array<App.HotelPackage>,
): RoomDetails | undefined {
  // Find a package with matching roomRateId
  const matchingPackage = packages.find(pkg => pkg.roomRate?.id === roomRateId)
  const roomType = matchingPackage?.roomType

  if (!roomType?.name || !roomType.sizeSqm) return undefined

  const bedConfiguration = roomType.bedGroups?.length
    ? roomType.bedGroups.map(bedGroup => bedGroup.description).join(' or ')
    : undefined

  return {
    name: roomType.name,
    size: `${roomType.sizeSqm}m²`,
    bedConfiguration,
    totalGuests: getMaxNonInfantGuestCount(matchingPackage?.roomRate?.capacities ?? []),
    numberOfBedrooms: roomType.numberOfBedrooms,
    numberOfBathrooms: roomType.numberOfBathrooms,
  }
}

export function createSearchSessionKey(params: {
  destinationId?: string
  propertyId?: string
  landmarkId?: string
  lat?: string
  lng?: string
  query?: string
}): string {
  const searchParams: Record<string, any> = {}

  if (params.destinationId) {
    searchParams.destinationId = params.destinationId
  } else if (params.propertyId) {
    searchParams.propertyId = params.propertyId
  } else if (params.landmarkId) {
    searchParams.landmarkId = params.landmarkId
  } else if (params.lat && params.lng) {
    searchParams.lat = params.lat
    searchParams.lng = params.lng
  } else if (params.query) {
    searchParams.query = params.query
  } else {
    searchParams.destinationId = ANYWHERE_PLACE_ID
  }

  return getSearchKey(searchParams)
}

/**
 * Validates if a date is within the acceptable booking range (today to 3 years from now)
 * @param date - The date to validate in ISO format
 * @param maxYears
 * @returns The date if valid, undefined if invalid
 */
export function validateExtractedCheckInOrCheckOutDate(date: string | undefined, maxYears: number = 3): string | undefined {
  if (!date) return undefined

  const targetDate = moment(date)
  const now = moment().startOf('day')
  const threeYearsFromNow = moment().add(maxYears, 'years').endOf('day')

  return targetDate.isAfter(now) && targetDate.isSameOrBefore(threeYearsFromNow)
    ? date
    : undefined
}

export function createNaturalLanguageItem(text: string, streamingData: App.TypeaheadStreamingData): App.SearchItemV3 {
  return {
    searchType: 'searchItemV3',
    value: text,
    format: {
      mainText: text,
    },
    display: {
      mainText: text,
      searchType: 'naturalLanguage',
    },
    searchItem: {
      typeaheadId: streamingData.typeaheadId,
      extractedData: mapTypeaheadStreamingDataToExtractedSearchParams(streamingData, text),
      // Currently we always set extracting to true for natural language items
      // Once we do extracting on each keystroke, we can change this to
      // extracting: streamingData.isComplete && streamingData.completedInput === text
      extracting: true,
    },
  }
}

interface StaticNaturalLanguageSuggestion {
  displayText: string
  query: string
  placeIds: Array<string>
  tags?: Array<string>
}

function createStaticNaturalLanguageSuggestion({
  displayText,
  query,
  placeIds,
  tags,
}: StaticNaturalLanguageSuggestion): App.SearchItemV3 {
  return {
    searchType: 'searchItemV3',
    value: displayText,
    format: {
      mainText: displayText,
    },
    display: {
      mainText: displayText,
      searchType: 'naturalLanguage',
    },
    searchItem: {
      typeaheadId: uuidV4(),
      extractedData: {
        // Keep backend search queries in English even when the visible suggestion text is localized.
        query,
        placeIds,
        searchType: 'destination',
        ...(tags ? { tags } : {}),
      },
      extracting: false,
    },
  }
}

export function getTypeaheadSuggestionsV2(regionCode?: string): Array<App.SearchItemV3> {
  const intl = getCurrentIntl()
  const isEuropeanRegion = !!regionCode && EUROPE_REGIONS.has(regionCode)
  const isGermanRegion = regionCode === 'DE'
  if (isEuropeanRegion) {
    return isGermanRegion ? [
      createStaticNaturalLanguageSuggestion({
        displayText: intl.formatMessage({
          id: 'jpP1G9343DpFqw4REG84',
          defaultMessage: 'Family Holiday in Mallorca',
          description: 'Natural language fallback search suggestion for family holidays in Mallorca on European storefronts',
        }),
        query: 'Family Holiday in Mallorca',
        placeIds: [Places.Mallorca.id],
        tags: ['Family Friendly'],
      }),
      createStaticNaturalLanguageSuggestion({
        displayText: intl.formatMessage({
          id: '0drlTamD8syTFOUV91NF',
          defaultMessage: 'Holiday packages in Thailand',
          description: 'Natural language fallback search suggestion for holiday packages in Thailand on European storefronts',
        }),
        query: 'Holiday packages in Thailand',
        placeIds: [Places.Thailand.id],
      }),
      createStaticNaturalLanguageSuggestion({
        displayText: intl.formatMessage({
          id: '6EkqPcwtIPS5b9YV14Vv',
          defaultMessage: 'Top hotels in Italy',
          description: 'Natural language fallback search suggestion for top hotels in Italy on European storefronts',
        }),
        query: 'Top hotels in Italy',
        placeIds: [Places.Italy.id],
      }),
    ] : [
      createStaticNaturalLanguageSuggestion({
        displayText: intl.formatMessage({
          id: 'Z6mICcvtX2vJOJxH65yk',
          defaultMessage: 'Family Holiday in Maldives',
          description: 'Natural language fallback search suggestion for family holidays in Maldives on European storefronts',
        }),
        query: 'Family Holiday in Maldives',
        placeIds: [Places.Maldives.id],
        tags: ['Family Friendly'],
      }),
      createStaticNaturalLanguageSuggestion({
        displayText: intl.formatMessage({
          id: 'gAfSg8nZy4coV0tPSEld',
          defaultMessage: 'Holiday Packages in Thailand',
          description: 'Natural language fallback search suggestion for holiday packages in Thailand on European storefronts',
        }),
        query: 'Holiday Packages in Thailand',
        placeIds: [Places.Thailand.id],
      }),
      createStaticNaturalLanguageSuggestion({
        displayText: intl.formatMessage({
          id: 'JC9LAqaFHgC2o03spdzd',
          defaultMessage: 'Top hotels in Greece',
          description: 'Natural language fallback search suggestion for top hotels in Greece on European storefronts',
        }),
        query: 'Top hotels in Greece',
        placeIds: [Places.Greece.id],
      }),
    ]
  }

  const familyHolidayInBaliQuery = 'Family holiday in bali'
  const holidayPackagesInFijiAndTheMaldivesQuery = 'Holiday packages in Fiji and the Maldives'
  const topHotelsForBeachGetawaysQuery = 'Top hotels for beach getaways'
  const familyHolidayInBali = intl.formatMessage({
    id: 'm3wQvPAlnGCGuo62DBg1',
    defaultMessage: 'Family holiday in bali',
    description: 'Natural language search suggestion example for a family holiday destination query in the search dropdown',
  })
  const holidayPackagesInFijiAndTheMaldives = intl.formatMessage({
    id: 'k4BeXPm61Y8mAYN280Tb',
    defaultMessage: 'Holiday packages in Fiji and the Maldives',
    description: 'Natural language search suggestion example for a multi-destination package query in the search dropdown',
  })
  const topHotelsForBeachGetaways = intl.formatMessage({
    id: 'HGjvkWXbJfr6JI4UdZA4',
    defaultMessage: 'Top hotels for beach getaways',
    description: 'Natural language search suggestion example for a beach-focused hotel query in the search dropdown',
  })

  return [
    createStaticNaturalLanguageSuggestion({
      displayText: familyHolidayInBali,
      query: familyHolidayInBaliQuery,
      placeIds: [Places.Bali.id],
      tags: ['Family Friendly'],
    }),
    createStaticNaturalLanguageSuggestion({
      displayText: holidayPackagesInFijiAndTheMaldives,
      query: holidayPackagesInFijiAndTheMaldivesQuery,
      placeIds: [Places.Fiji.id, Places.Maldives.id],
    }),
    createStaticNaturalLanguageSuggestion({
      displayText: topHotelsForBeachGetaways,
      query: topHotelsForBeachGetawaysQuery,
      placeIds: [
        Places.Bali.id,
        Places.Fiji.id,
        Places.Maldives.id,
        Places.Phuket.id,
        Places.KohSamui.id,
        Places.KhaoLak.id,
        Places.GoldCoast.id,
        Places.Hawaii.id,
        Places.SunshineCoast.id,
      ],
    }),
  ]
}

/**
 * Maps occupancies to the format expected by typeahead extract events
 */
export function mapOccupanciesToTypeaheadFormat(
  rooms: Array<App.Occupants>,
): Array<App.TypeaheadExtractedOccupancy> {
  return rooms.map(room => ({
    adults: room.adults,
    childrenAges: room.childrenAge ?? [],
  }))
}

/**
 * Maps flexible date selections (months + nights) to the format expected by typeahead extract events
 */
export function mapFlexibleDatesToTypeaheadFormat(
  flexibleMonths: string,
  flexibleNights: FLEXIBLE_DURATION_RANGE,
): App.TypeaheadExtractedTravelDates {
  const monthStrings = flexibleMonths.split(',').filter(Boolean)
  const dateRanges = monthStrings.map(monthStr => {
    const monthMoment = moment(monthStr, 'YYYY-MM')
    return {
      start: monthMoment.startOf('month').format(ISO_DATE_FORMAT),
      end: monthMoment.endOf('month').format(ISO_DATE_FORMAT),
    }
  })
  const numberOfNights = flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
    ? []
    : flexibleNights.split('-').map(n => parseInt(n, 10)).filter(n => !isNaN(n))

  return {
    type: 'flexible',
    dateRanges,
    numberOfNights,
  }
}

/**
 * Maps date selections to the format expected by typeahead extract events
 */
export function mapDatesToTypeaheadFormat(
  checkinDate: Moment | undefined,
  checkoutDate: Moment | undefined,
  flexibleNights: FLEXIBLE_DURATION_RANGE | undefined,
  flexibleMonths: string | undefined,
  isAnytimeDateSelected: boolean,
): App.TypeaheadExtractedTravelDates | undefined {
  if (checkinDate && checkoutDate) {
    return {
      type: 'specific',
      checkIn: checkinDate.format(ISO_DATE_FORMAT),
      checkOut: checkoutDate.format(ISO_DATE_FORMAT),
    }
  }

  if (flexibleNights && flexibleMonths) {
    return mapFlexibleDatesToTypeaheadFormat(flexibleMonths, flexibleNights)
  }

  if (isAnytimeDateSelected) {
    return { type: 'anytime' }
  }

  return undefined
}
